import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { CustomsStateNotificationType, SortDirection, TransitGroup, TransitStatus } from '@e-origin/shared';

import {
  TransitAmendDataSchema,
  TransitCreateDataSchema,
  TransitDuplicateDataSchema,
  TransitEditDataSchema,
  TransitGenerateReportDataSchema,
  TransitInvalidateSchema,
  TransitListItemSchema,
  TransitNonArrivalInfoSchema,
  TransitRequestReleaseSchema,
  TransitSchema,
  TransitUploadHouseItemsDataSchema,
} from './schemas';
import { TransitHouseConsignmentSchema } from './schemas/TransitConsignmentSchema';

const c = initContract();

export const transitContract = c.router({
  retrieveTransits: {
    method: 'POST',
    path: '/transits',
    body: z.object({
      criteria: z
        .object({
          filters: z.object({
            group: z.nativeEnum(TransitGroup),
            searchQuery: z.string().optional(),
            status: z.array(z.nativeEnum(TransitStatus)).nullable().optional(),
            filterByUsers: z.array(z.string()).optional(),
            filterByNotifications: z.array(z.nativeEnum(CustomsStateNotificationType)).optional(),
          }),
          pagination: z.object({ page: z.number(), size: z.number() }),
          sorting: z.object({ field: z.string(), direction: z.enum([SortDirection.ASC, SortDirection.DESC]) }),
        })
        .optional(),
    }),
    responses: {
      200: z.object({
        data: z.object({
          list: z.array(TransitListItemSchema),
          totalItems: z.number(),
        }),
      }),
    },
  },
  retrieveTransit: {
    method: 'GET',
    path: '/transits/:id',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: z.object({
        data: z.object({ transit: TransitSchema }),
      }),
      404: z.object({ message: z.string() }),
    },
  },
  createTransit: {
    method: 'POST',
    path: '/transits/add',
    body: TransitCreateDataSchema,
    responses: {
      200: z.object({ data: TransitListItemSchema }),
    },
  },
  deleteTransit: {
    method: 'DELETE',
    path: '/transits/:id',
    body: c.noBody(),
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: z.object({}),
    },
  },
  updateTransit: {
    method: 'PATCH',
    path: '/transits/update',
    body: TransitEditDataSchema,
    responses: {
      200: z.object({ data: TransitSchema }),
      404: z.object({ message: z.string() }),
    },
  },
  duplicateTransit: {
    method: 'POST',
    path: '/transits/duplicate',
    body: TransitDuplicateDataSchema,
    responses: {
      200: z.object({ data: z.object({ _id: z.string() }) }),
    },
  },
  amendTransit: {
    method: 'POST',
    path: '/transits/:transitId/amend',
    pathParams: z.object({ transitId: z.string() }),
    body: TransitAmendDataSchema,
    responses: {
      200: z.object({ data: z.object({ _id: z.string() }) }),
    },
  },
  sendTransitToNcts: {
    method: 'POST',
    path: '/transits/:transitId/send-transit-to-ncts',
    pathParams: z.object({ transitId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ data: z.object({ message: z.string() }) }),
    },
  },
  customsSyncTransit: {
    method: 'POST',
    path: '/transits/:transitId/customs-sync',
    pathParams: z.object({ transitId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ data: z.any() }),
    },
  },
  generateNctsReport: {
    method: 'POST',
    path: '/transits/generate-ncts-report',
    body: TransitGenerateReportDataSchema,
    responses: {
      200: z.string(),
      400: z.object({ message: z.string() }),
    },
  },
  generateNctsFollowLetter: {
    method: 'POST',
    path: '/transits/generate-ncts-follow-letter',
    body: TransitGenerateReportDataSchema,
    responses: {
      200: z.string(),
      400: z.object({ message: z.string() }),
    },
  },
  downloadNctsReport: {
    method: 'GET',
    path: '/transits/:id/download-ncts-report',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: c.otherResponse({ contentType: 'application/pdf', body: z.any() }),
    },
  },
  downloadNctsFollowLetter: {
    method: 'GET',
    path: '/transits/:id/download-ncts-follow-letter',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: c.otherResponse({ contentType: 'application/pdf', body: z.any() }),
    },
  },
  invalidateTransit: {
    method: 'POST',
    path: '/transits/invalidate',
    body: TransitInvalidateSchema,
    responses: {
      200: z.object({}),
    },
  },
  requestRelease: {
    method: 'POST',
    path: '/transits/request-release',
    body: TransitRequestReleaseSchema,
    responses: {
      200: z.object({}),
    },
  },
  sendNonArrivalInfo: {
    method: 'POST',
    path: '/transits/send-non-arrival-info',
    body: TransitNonArrivalInfoSchema,
    responses: {
      200: z.object({}),
    },
  },
  downloadHouseConsignmentItems: {
    method: 'GET',
    path: '/transits/:transitId/:houseConsignmentIndex/download-house-consignment-items',
    pathParams: z.object({ transitId: z.string(), houseConsignmentIndex: z.string() }),
    responses: {
      200: c.otherResponse({
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        body: z.any(),
      }),
    },
  },
  downloadAllHouseConsignmentItems: {
    method: 'GET',
    path: '/transits/:id/download-all-house-consignment-items',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: c.otherResponse({
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        body: z.any(),
      }),
    },
  },
  uploadHouseConsignmentItems: {
    method: 'POST',
    path: '/transits/:transitId/:houseConsignmentIndex/upload-house-consignment-items',
    pathParams: z.object({ transitId: z.string(), houseConsignmentIndex: z.string() }),
    contentType: 'multipart/form-data',
    body: TransitUploadHouseItemsDataSchema,
    responses: {
      200: z.object({ data: TransitHouseConsignmentSchema }),
    },
  },
  markCustomsNotificationAsRead: {
    method: 'PATCH',
    path: '/transits/:transitId/customs-notifications/:notificationId/read',
    pathParams: z.object({ transitId: z.string(), notificationId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
});
